import { useRef, useState } from "react";
import useModalDialog from "../../hooks/useModalDialog";
import { WorldButton, WorldFrame, WorldShell, WorldWordmark } from "./WorldPrimitives";
import "./foundation-preview.css";

const LANTERN_GLYPH = "/glyphs/tgc-124/lantern-v1.png";

export default function FoundationPreview() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);

  useModalDialog({
    isOpen: dialogOpen,
    dialogRef,
    returnFocusRef: triggerRef,
    onClose: () => setDialogOpen(false),
  });

  return (
    <WorldShell mode="operate" aria-labelledby="foundation-title">
      <p className="foundation-preview__dev-note">
        TGC-130 reference specimen · development preview only
      </p>

      <WorldFrame className="foundation-preview">
        <WorldWordmark />

        <section className="foundation-preview__operate" aria-labelledby="foundation-title">
          <p className="foundation-preview__mode">Operate</p>
          <div className="foundation-preview__task">
            <p className="foundation-preview__counter">Question 1 of 12</p>
            <h1 className="tgc-world-title" id="foundation-title">What’s your name?</h1>
            <label className="foundation-preview__field-label" htmlFor="foundation-input">
              Your first name
            </label>
            <input
              className="tgc-world-field"
              id="foundation-input"
              placeholder="Your first name"
              aria-describedby="foundation-validation"
              aria-invalid="true"
            />
            <p className="tgc-world-validation" id="foundation-validation">
              We need a name to continue.
            </p>
            <div className="foundation-preview__actions">
              <WorldButton variant="secondary">Back</WorldButton>
              <WorldButton>Next</WorldButton>
            </div>
          </div>

          <aside className="foundation-preview__states" aria-label="Shared feedback states">
            <div className="tgc-world-state" role="status" aria-live="polite">
              <span className="tgc-world-busy" aria-hidden="true" />
              <span>Loading...</span>
            </div>
            <p className="tgc-world-state tgc-world-state--success" role="status">Link copied!</p>
            <p className="tgc-world-state tgc-world-state--empty">Just a moment</p>
            <WorldButton disabled>Next</WorldButton>
          </aside>
        </section>

        <section className="foundation-preview__experience" aria-labelledby="foundation-experience-title">
          <div
            className="foundation-preview__threshold"
            aria-hidden="true"
            style={{ "--tgc-threshold-reference": "url('/backgrounds/tgc-124/threshold-continuation-v1.png')" }}
          >
            <img src={LANTERN_GLYPH} alt="" />
          </div>
          <div className="foundation-preview__experience-copy">
            <p className="foundation-preview__mode">Experience</p>
            <h2 id="foundation-experience-title">The conversation becomes a keepsake</h2>
            <p>
              When the conversation ends, they receive a written guide built from everything they shared — so any AI can actually be useful to them from the first message.
            </p>
            <WorldButton ref={triggerRef} variant="quiet" onClick={() => setDialogOpen(true)}>
              more
            </WorldButton>
          </div>
        </section>

        <article className="foundation-preview__read" aria-labelledby="foundation-read-title">
          <p className="foundation-preview__mode">Read</p>
          <h2 id="foundation-read-title">This experience uses AI, and always tells you when.</h2>
          <p>
            They open a link and talk — by text or voice — with an AI that already knows a few things about them. No script, no agenda. Just a real conversation.
          </p>
          <p>
            Most gifts get used once. This one gets used every time they open an AI.
          </p>
        </article>
      </WorldFrame>

      {dialogOpen && (
        <div className="tgc-world-modal-layer" data-modal-layer role="presentation">
          <section
            className="tgc-world-modal"
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="foundation-dialog-title"
            tabIndex={-1}
          >
            <h2 id="foundation-dialog-title">A gift they keep</h2>
            <p className="tgc-world-copy">
              The keepsake is a custom prompt they can paste into ChatGPT, Claude, or any AI — so they don't have to start from scratch. They can use it for homework, work projects, or just talking through things. Wherever they use it, they can start as themselves instead of explaining themselves first.
            </p>
            <WorldButton onClick={() => setDialogOpen(false)}>Close</WorldButton>
          </section>
        </div>
      )}
    </WorldShell>
  );
}
